import { useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";

interface VideoHeroProps {
  videoUrl?: string;
  youtubeId?: string;
  wistiaId?: string;
  className?: string;
}

export default function VideoHero({ videoUrl, youtubeId, wistiaId, className = "" }: VideoHeroProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (wistiaId) {
      // Load Wistia scripts
      const script1 = document.createElement('script');
      script1.src = 'https://fast.wistia.com/player.js';
      script1.async = true;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.src = `https://fast.wistia.com/embed/${wistiaId}.js`;
      script2.async = true;
      script2.type = 'module';
      document.head.appendChild(script2);

      // Add the blur style for loading state
      const style = document.createElement('style');
      style.textContent = `
        wistia-player[media-id='${wistiaId}']:not(:defined) { 
          background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${wistiaId}/swatch'); 
          display: block; 
          filter: blur(5px); 
          padding-top: 56.25%; 
        }
      `;
      document.head.appendChild(style);

      return () => {
        // Cleanup scripts and styles on unmount
        const scripts = document.querySelectorAll(`script[src*="${wistiaId}"], script[src*="wistia"]`);
        scripts.forEach(script => script.remove());
        const styles = document.querySelectorAll('style');
        styles.forEach(styleEl => {
          if (styleEl.textContent?.includes(wistiaId)) {
            styleEl.remove();
          }
        });
      };
    }
  }, [wistiaId]);

  if (wistiaId) {
    return (
      <div className={`relative w-full max-w-4xl mx-auto ${className}`}>
        <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
          <wistia-player 
            media-id={wistiaId} 
            aspect="1.7777777777777777"
            className="w-full h-full"
          />
        </div>
      </div>
    );
  }

  if (youtubeId) {
    return (
      <div className={`relative w-full max-w-4xl mx-auto ${className}`}>
        <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
            title="CraftyJoy Demo Video"
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      </div>
    );
  }

  if (videoUrl) {
    return (
      <div className={`relative w-full max-w-4xl mx-auto ${className}`}>
        <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
          <video
            className="w-full h-full object-cover"
            controls
            poster="/video-poster.jpg"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    );
  }

  // Placeholder when no video is provided
  return (
    <div className={`relative w-full max-w-4xl mx-auto ${className}`}>
      <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-primary/10 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-primary/20 rounded-full flex items-center justify-center">
            <Play className="w-8 h-8 text-primary ml-1" />
          </div>
          <p className="text-lg text-muted-foreground">Demo video coming soon</p>
        </div>
      </div>
    </div>
  );
}